import { PropertyKind } from "@interfaces/properties.interface";
import { Amenities } from "@interfaces/requests.interface";
import "@react-google-maps/api";
import {
  getPropertyAmenities,
  getPropertyKinds,
} from "@services/krent.service";
import ktoast from "@services/toast.service";
import { formatCurrency } from "@utils/currency";
import { addPageToSessionStorage } from "@utils/helpers";
import { useEffect, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import Select, {
  CSSObjectWithLabel,
  MultiValue,
  StylesConfig,
} from "react-select";

export const FiltersCard = ({
  autoCompleteLocation,
  setAutoCompleteLocation,
  page,
  limit,
  setPage,
  setFiltersLoaded,
}: any) => {
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(0);
  const [minSize, setMinSize] = useState<number>(0);
  const [maxSize, setMaxSize] = useState<number>(0);
  const [propertyType, setPropertyType] = useState<string>("");
  const [propertyKinds, setPropertyKinds] = useState<PropertyKind[]>([]);
  const [propertyLocation, setPropertyLocation] = useState<string>("");
  const [bedroom, setBedroom] = useState<string>("");
  const [bathroom, setBathroom] = useState<string>("");
  const [allAmenities, setAllAmenities] = useState<Amenities[]>([]);
  const [amenities, setAmenities] = useState<Amenities[]>([]);
  const navigate = useNavigate();
  const location: any = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initialIntent = urlParams.get("intent");
  const [intent, setIntent] = useState<string | any>(initialIntent || "");
  const [isLoadingAmenities, setLoadingAmenities] = useState(false);
  const caps = (string: any) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  type SelectOption = {
    value: string;
    label: string;
  };

  const customStyles: StylesConfig<SelectOption, true> = {
    control: (base) =>
      ({
        ...base,
        borderRadius: "3rem",
        padding: "2px 8px",
      } as CSSObjectWithLabel),
    multiValue: (base) =>
      ({
        ...base,
        borderRadius: "2rem",
        padding: "0 4px",
      } as CSSObjectWithLabel),
  };
  const onSearch = (): void => {
    let params: URLSearchParams = new URLSearchParams();
    if (bedroom) params.append("bedrooms", bedroom);
    if (bathroom) params.append("bathrooms", bathroom);
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());
    if (intent) params.append("intent", intent);
    if (propertyType) params.append("kind.title", propertyType);
    if (maxPrice) params.append("price[lt]", maxPrice.toString());
    if (minPrice) params.append("price[gt]", minPrice.toString());
    if (maxSize) params.append("size[lte]", maxSize.toString());
    if (minSize) params.append("size[gte]", minSize.toString());
    if (autoCompleteLocation) {
      params.append("location.city", autoCompleteLocation);
    } else if (propertyLocation) {
      params.append("location.city", caps(propertyLocation));
    }

    amenities.forEach((amenity) => {
      amenity.offers.forEach((offer) => {
        params.append(`amenities[${amenity.title}]`, offer);
      });
    });

    // Check for existing query parameters in the current URL
    const existingSearchParams = new URLSearchParams(window.location.search);
    const existingParamsArray = Array.from(existingSearchParams.entries());
    for (const [key, value] of existingParamsArray) {
      // If the query parameter is not already set in our params, append it
      if (!params.has(key)) {
        params.append(key, value);
      }
    }

    navigate(
      {
        pathname: "/properties/",
        search: `?${createSearchParams(params)}`,
      },
      { replace: false }
    );
  };

  // const changePage = (page: number) => {
  //   setPage(page);
  // };

  const handleSearch = () => {
    // reset the page back to 1 if filtering
    addPageToSessionStorage(1);
    setFiltersLoaded(true);

    setPage(1);
    onSearch();
    setAutoCompleteLocation("");
    setPropertyLocation("");
    setPropertyType("");
    setMaxPrice(0);
    setMinPrice(0);
    setMaxSize(0);
    setMinSize(0);
    setBathroom("");
    setBedroom("");
    setIntent("");
  };

  const handleClearFilters = () => {
    setAutoCompleteLocation("");
    setPropertyLocation("");
    setPropertyType("");

    setMaxPrice(0);
    setMinPrice(0);
    setMaxSize(0);
    setMinSize(0);
    setBathroom("");
    setBedroom("");
    setIntent("");
    setFiltersLoaded(false);
  };

  const onChangeSlider = (
    e: React.ChangeEvent<HTMLInputElement>,
    callback: Function
  ): void => {
    callback(Number(e.target.value));
  };

  const handleOnChange = (
    selectedOptions: MultiValue<SelectOption>,
    f: Amenities,
    title: string
  ) => {
    console.log(title);
    const tempOffers = selectedOptions.map((item) => item.value);
    const newAmenity: Amenities = {
      icon: "",
      offers: tempOffers,
      title: f.title,
    };

    setAmenities((prev) => {
      const existingIndex = prev.findIndex((item) => item.title === f.title);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newAmenity;
        return updated;
      }
      return [...prev, newAmenity];
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    setLoadingAmenities(true);

    getPropertyAmenities(abortController.signal)
      .then((resp: any) => {
        if (!Array.isArray(resp?.data)) {
          throw new Error("Invalid amenities data format");
        }

        const filteredAmenities = resp.data.filter(
          (amenity: any) =>
            typeof amenity === "object" &&
            amenity !== null &&
            "title" in amenity
        ) as Amenities[];

        setAllAmenities(filteredAmenities);
      })
      .catch((err) => {
        ktoast.error(err?.message || "Failed to load amenities");
      })
      .finally(() => {
        setLoadingAmenities(false);
      });

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();

    getPropertyKinds(abortController.signal)
      .then((res: any) => {
        if (!Array.isArray(res?.data)) {
          throw new Error("Invalid property kinds data format");
        }

        const validPropertyKinds = res.data.filter(
          (item: any): item is PropertyKind =>
            typeof item === "object" &&
            item !== null &&
            "id" in item &&
            "title" in item
        );

        setPropertyKinds(validPropertyKinds);
      })
      .catch((err) => {
        ktoast.error(err?.message || "Failed to load property types");
      });

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <div className="offcanvas-body p-4">
      {/* location */}
      <div className="row mb-5">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Location
        </label>
        <div className="col-sm-6">
          <input
            type="text"
            className=" fw-bold form-control fs-16 rounded-pill"
            value={propertyLocation}
            name="property location"
            onChange={(e) => {
              setPropertyLocation(e.target.value);
            }}
          />
        </div>
      </div>
      {/* Property Size */}
      <div className="row mb-5">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Size
        </label>
        <div className="col-sm-6">
          <div className="dropdown">
            <button
              type="button"
              className="btn btn-transparent border-0"
              id="sizeDropdown"
              aria-haspopup="true"
              aria-expanded="false"
              data-bs-toggle="dropdown"
            >
              <span className="fw-bold">
                {minSize}m - {maxSize}m
              </span>
              <i className="iconly-Arrow-Down-Circle icbo ml-2"></i>
            </button>
            <div
              className="dropdown-menu"
              aria-labelledby="dropdown"
              style={{ width: 400 }}
            >
              <div className="container">
                <div className="row">
                  <div className="col-6">
                    <label htmlFor="customRange1" className="form-label">
                      Min
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      id="customRange1"
                      max={300}
                      defaultValue={120}
                      onChange={(e) => onChangeSlider(e, setMinSize)}
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="customRange2" className="form-label">
                      Max
                    </label>
                    <input
                      type="range"
                      className="form-range"
                      id="customRange2"
                      max={5000}
                      defaultValue={150}
                      // step={10000}
                      onChange={(e) => onChangeSlider(e, setMaxSize)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Price */}
      <div className="row mb-5">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Price
        </label>
        <div className="col-sm-6">
          <div className="dropdown">
            <button
              type="button"
              className="btn btn-transparent border-0"
              id="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              data-bs-toggle="dropdown"
            >
              <span className="fw-bold">
                ₦{formatCurrency(minPrice)} - ₦{formatCurrency(maxPrice)}
              </span>
              <i className="iconly-Arrow-Down-Circle icbo ml-2"></i>
            </button>
            <div
              className="dropdown-menu"
              aria-labelledby="dropdown"
              style={{ minWidth: 400 }}
            >
              <div className="container">
                <div className="row">
                  <div className="col-6">
                    <label htmlFor="customRange1" className="form-label">
                      Min
                    </label>
                    {
                      <span className="fs-12 d-block">
                        {" "}
                        ₦{formatCurrency(minPrice)}
                      </span>
                    }
                    <input
                      type="number"
                      className="border-1 fw-bold form-control fs-16"
                      // id='customRange1'
                      // max={500}
                      name="minPrice"
                      value={minPrice}
                      onChange={(e) => {
                        setMinPrice(parseInt(e.target.value));
                      }}
                    />
                  </div>
                  <div className="col-6">
                    <label htmlFor="customRange2" className="form-label">
                      Max
                    </label>
                    {
                      <span className="fs-12 d-block">
                        {" "}
                        ₦{formatCurrency(maxPrice)}
                      </span>
                    }
                    <input
                      type="number"
                      className="border-1 fw-bold form-control fs-16"
                      // id='customRange1'
                      // max={500}
                      name="maxPrice"
                      value={maxPrice}
                      onChange={(e) => {
                        setMaxPrice(parseInt(e.target.value));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* property type */}
      <div className="row mb-5">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Property Type
        </label>
        <div className="col-sm-6">
          <select
            className="form-select rounded-pill py-2 px-3"
            onChange={(e) => setPropertyType(e.target.value)}
            value={propertyType}
          >
            <option value="">Select</option>
            {propertyKinds?.map(({ id, title }) => {
              return (
                <option key={id} value={title}>
                  {title}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="mb-5 row">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Intent
        </label>
        <div className="col-sm-6">
          <select
            className="form-select rounded-pill py-2 px-3"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
          >
            <option value="">Select</option>
            <option value="shortlet">shortlet</option>
            <option value="long lease">long lease</option>
            <option value="sale">sale</option>
          </select>
        </div>
      </div>
      <div className="mb-5 row">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Bedroom
        </label>
        <div className="col-sm-6">
          <select
            className="form-select rounded-pill py-2 px-3"
            value={bedroom}
            onChange={(e) => setBedroom(e.target.value)}
          >
            <option value="">Select</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>
      </div>

      {/* Bathrooms */}
      <div className="mb-5 row">
        <label htmlFor="staticEmail" className="col-sm-6 col-form-label fw-600">
          Bathrooms
        </label>
        <div className="col-sm-6">
          <select
            className="form-select rounded-pill py-2 px-3"
            value={bathroom}
            onChange={(e) => setBathroom(e.target.value)}
          >
            <option value="">Select</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>
      </div>
      <div>
        <h5
          className="offcanvas-title fw-bolder mb-3"
          id="offcanvasExampleLabel"
        >
          Filters by amenities
        </h5>
        {isLoadingAmenities ? (
          <p>Loading amenities...</p>
        ) : (
          allAmenities?.map((f: Amenities, i: number) => {
            const { offers, title, id } = f;
            const opt = offers.map((o: any) => {
              return { label: o, value: o };
            });
            const existing =
              amenities && amenities?.map((item: any) => item.offers);

            return (
              <div className="form-group mb-4" key={id}>
                <label className="fw-bold mb-2">{title}</label>
                <Select
                  isMulti
                  name={title}
                  options={opt}
                  className="basic-multi-select rounded-full"
                  classNamePrefix="form-select"
                  onChange={(e) => handleOnChange(e, f, title)}
                  aria-label={`Select ${title} amenities`}
                  placeholder="Select..."
                  styles={customStyles}
                />

                <div className="row hidden">
                  {existing &&
                    existing[i]?.map((item: string[], index: number) => (
                      <div className="col-6 col-md-3" key={index}>
                        <div className="border border-round-xl p-1 mt-2 fs-8 text-center border-label">
                          {item}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="row">
        <button
          className="btn btn-custom-primary  "
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={handleSearch}
        >
          Search
        </button>
        <button
          className="btn btn-custom-secondary mt-3"
          onClick={handleClearFilters}
        >
          Clear
        </button>
      </div>
    </div>
  );
};
